import { QualificationJSON, QualificationType } from '@scale/shared/types/qualification/types';
import { Combobox } from '@headlessui/react';
import { useState, forwardRef, useCallback, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import { Icon, Typography } from '@scale/scaleui';

import { WorkerSkillResponse } from '@scale/genai-data-service-client';
import { defaultWorkerSkillMedal } from './constants';
import { defaultWorkerSkillDimension } from '@scale/shared-types';
import { TreeItem, TreeItemContentProps, TreeView, useTreeItem } from '@mui/x-tree-view';
import { QualificationWithAlternates, useQualificationsStore } from './store';
import {
  WorkerSkillDimension,
  WorkerSkillDimensionQualityCategory,
} from '@scale/shared/workerSkills/types';
import clsx from 'clsx';
import _ from 'lodash';

declare module '@mui/x-tree-view/TreeItem' {
  interface TreeItemContentProps {
    option: WorkerSkillTreeNode;
    setSelectedOption: (option: WorkerSkillTreeNode) => void;
  }
}

interface SearchWorkerSkillsMenuProps {
  onSelect: (qualification: QualificationWithAlternates) => void;
  onlyUseWorkerSkills?: boolean;
  selectedWorkerSkill?: WorkerSkillTreeNode;
  onSelectWorkerSkill?: (workerSkillNode: WorkerSkillTreeNode) => void;
  onBlur?: () => void;
  className?: string;
  disabled?: boolean;
  preventDefaultSelection?: boolean;
  placeholder?: string;
}

export interface WorkerSkillTreeNode {
  name: string;
  description?: string;
  id: string;
  children: WorkerSkillTreeNode[];
  qualifications?: QualificationJSON[];
}

export const SearchWorkerSkillsMenu: React.FC<SearchWorkerSkillsMenuProps> = ({
  onSelect,
  onlyUseWorkerSkills,
  selectedWorkerSkill,
  onSelectWorkerSkill,
  className,
  onBlur,
  disabled = false,
  preventDefaultSelection = false,
  placeholder,
}) => {
  const [query, setQuery] = useState('');
  const [expandedNodeIds, setExpandedNodeIds] = useState<string[]>([]);
  const [workerSkillTrees, setWorkerSkillTrees] = useState<WorkerSkillTreeNode[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<WorkerSkillTreeNode[]>([]);

  const { allQualifications, allWorkerSkills } = useQualificationsStore();

  /**
   * Constructs trees of worker skills mapped to their corresponding qualifications.
   * @return The root nodes of the trees
   */
  const constructSkillTrees = useEffect(() => {
    // Construct map of worker skill id to corresponding qualifications
    const qualIdMap = onlyUseWorkerSkills
      ? undefined
      : allQualifications
          .filter(
            q =>
              q.qualificationType === QualificationType.WorkerSkill &&
              q.metadata.workerSkillDimension === WorkerSkillDimension.Overall &&
              q.metadata.workerSkillStatus !== WorkerSkillDimensionQualityCategory.Unranked,
          )
          .map(q => [q.metadata.workerSkillId, q])
          .reduce((curr: Map<string, QualificationJSON[]>, [workerSkillId, qual]) => {
            if (!curr.get(workerSkillId)) {
              curr.set(workerSkillId, []);
            }
            curr.get(workerSkillId)?.push(qual);
            return curr;
          }, new Map<string, QualificationJSON[]>());
    // This is a type hack to fix the WorkerSkillResponse type here, but fixing it upstream leads to a mess
    function mapSkillToTreeNode(
      skill: WorkerSkillResponse & {
        internalDescription?: string;
        externalDescription?: string;
        displayName?: string;
      },
    ): WorkerSkillTreeNode {
      return {
        name: skill.displayName || skill.name,
        description: skill.internalDescription || skill.externalDescription,
        id: skill.id,
        qualifications: qualIdMap?.get(skill.id) || [],
        children: onlyUseWorkerSkills
          ? skill.childSkills?.map(mapSkillToTreeNode) || []
          : skill.childSkills?.filter(s => qualIdMap?.has(s.id)).map(mapSkillToTreeNode) || [],
      };
    }

    const nodes = onlyUseWorkerSkills
      ? allWorkerSkills.map(mapSkillToTreeNode)
      : allWorkerSkills.filter(s => qualIdMap?.has(s.id)).map(mapSkillToTreeNode);
    setWorkerSkillTrees(nodes);
  }, [allQualifications, allWorkerSkills, onlyUseWorkerSkills]);

  /**
   * Filters the nodes within each of the given trees using the query string. If a node's name contains the query string,
   * all its ancestors and children should remain in the tree.
   * @param query Query to match the skill name against, ignoring case
   * @param workerSkillTrees List of root tree nodes to filter
   */
  useEffect(() => {
    if (query === '') {
      setFilteredOptions(workerSkillTrees);
    }

    function filterTree(node: WorkerSkillTreeNode): [boolean, WorkerSkillTreeNode | null] {
      let includesQuery = JSON.stringify(node).toLowerCase().includes(query.toLowerCase());
      let expanded = false;
      const filteredChildren: WorkerSkillTreeNode[] = [];

      for (const child of node.children) {
        const [childIncludesQuery, filteredChild] = filterTree(child);
        if (childIncludesQuery && filteredChild) {
          filteredChildren.push(filteredChild);
          includesQuery = true;
          // If a child includes the query, the parent should be expanded
          expanded = true;
        }
      }

      if (expanded) {
        setExpandedNodeIds(e => _.uniq([...e, node.id]));
      }

      return [
        includesQuery,
        includesQuery ? { ...node, children: filteredChildren.slice(0, 100) } : null,
      ];
    }

    setFilteredOptions(
      workerSkillTrees
        .map(tree => filterTree(tree)[1])
        .filter(tree => tree !== null) as WorkerSkillTreeNode[],
    );
  }, [query, constructSkillTrees, workerSkillTrees]);

  // Clear the search query when an option is selected
  const handleSelection = (optionNode: WorkerSkillTreeNode) => {
    setQuery(''); // Clear the search query
    if (!preventDefaultSelection) {
      setSelectedOption(optionNode);
    }
  };

  function setSelectedOption(optionNode: WorkerSkillTreeNode) {
    if (onlyUseWorkerSkills) {
      if (onSelectWorkerSkill) {
        onSelectWorkerSkill(optionNode);
      }
      return;
    }
    if (!optionNode.qualifications) {
      return;
    }

    const qualification =
      optionNode.qualifications.find(
        q =>
          q.metadata.workerSkillDimension === defaultWorkerSkillDimension &&
          q.metadata.workerSkillStatus === defaultWorkerSkillMedal,
      ) ||
      optionNode.qualifications.find(
        q => q.metadata.workerSkillDimension === defaultWorkerSkillDimension,
      ) ||
      optionNode.qualifications.find(
        q => q.metadata.workerSkillStatus === defaultWorkerSkillMedal,
      ) ||
      (optionNode.qualifications.length > 0 ? optionNode.qualifications[0] : undefined);

    if (!qualification) {
      // Qualification does not exist for dimension or category
      return;
    }

    onSelect({
      ...qualification,
      altDimensions: optionNode.qualifications.filter(q => q !== qualification),
    });
  }

  const handleToggleExpand = useCallback((_: any, nodeIds: string[]) => {
    setExpandedNodeIds(nodeIds);
  }, []);

  const TreeItemOptionContent = forwardRef(function TreeItemOptionContent(
    props: TreeItemContentProps,
    ref,
  ) {
    const {
      classes,
      className,
      label,
      nodeId,
      icon: iconProp,
      expansionIcon,
      displayIcon,
      option,
      setSelectedOption,
    } = props;

    const {
      disabled,
      expanded,
      selected,
      focused,
      handleExpansion,
      handleSelection,
      preventSelection,
    } = useTreeItem(nodeId);

    const icon = iconProp || expansionIcon || displayIcon;

    const handleMouseDown = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      event.preventDefault();
      preventSelection(event);
    };

    const handleExpansionClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      event.preventDefault();
      handleExpansion(event);
    };

    const handleSelectionClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      setSelectedOption(option);
      handleSelection(event);
    };

    return (
      <div
        className={clsx(className, classes.root, {
          [classes.expanded]: expanded,
          [classes.selected]: selected,
          [classes.focused]: focused,
          [classes.disabled]: disabled,
        })}
        onMouseDown={handleMouseDown}
        ref={ref as React.Ref<HTMLDivElement>}
      >
        <div onClick={handleExpansionClick} className={classes.iconContainer}>
          {icon}
        </div>
        <div onClick={handleSelectionClick} className={classes.label}>
          <Combobox.Option
            key={option.id}
            value={option}
            className="relative cursor-default select-none py-1 px-3 text-neutral-800 flex gap-3 items-center"
          >
            {({ selected }) => (
              <div className="flex flex-row gap-2 items-center">
                <div className="flex items-center gap-2">
                  <Typography.Subtitle3>{label}</Typography.Subtitle3>
                  {option.description && (
                    <Icon
                      icon={['fas', 'info']}
                      rounded
                      size="sm"
                      tooltip={option.description}
                      variant="neutral"
                    />
                  )}
                  {selected && <Icon icon="check" />}
                </div>
              </div>
            )}
          </Combobox.Option>
        </div>
      </div>
    );
  });

  interface TreeOptionsProps {
    opts: WorkerSkillTreeNode[];
    level?: number;
    setSelectedOption: (option: WorkerSkillTreeNode) => void;
  }

  const TreeOptions: React.FC<TreeOptionsProps> = ({ opts, level = 0, setSelectedOption }) => {
    if (opts.length === 0) {
      return null;
    }

    return (
      <>
        {opts.map((opt, i) => (
          <TreeItem
            key={i}
            collapseIcon={opt.children.length && <Icon icon="caret-down" size="lg" />}
            expandIcon={opt.children.length && <Icon icon="caret-right" size="lg" />}
            nodeId={opt.id}
            label={`${opt.name}${opt.children.length > 0 ? ` (${opt.children.length})` : ''}`}
            ContentComponent={TreeItemOptionContent}
            ContentProps={{ option: opt, setSelectedOption } as any}
            classes={{
              iconContainer: `w-16 pl-${level * 4}`,
            }}
          >
            <TreeOptions
              opts={opt.children}
              level={level + 1}
              setSelectedOption={setSelectedOption}
            />
          </TreeItem>
        ))}
      </>
    );
  };

  return (
    <div
      className={twMerge(
        'scaleui scale-theme scale-tailwind relative [&_.MuiTooltip-popper]:!z-[9999]',
        disabled && 'opacity-50 pointer-events-none',
        className,
      )}
      {...(onBlur && { onBlur })}
    >
      {/* TODO: fix virtual prop and enable multiple */}
      <Combobox
        by="name"
        value={selectedWorkerSkill}
        onChange={handleSelection}
        disabled={disabled}
      >
        {({ open }) => (
          <>
            <Combobox.Button
              as="div"
              className={twMerge(
                'relative w-full py-2 pl-3 pr-10 text-left border rounded-lg shadow-sm cursor-default focus:outline-none focus:ring-1 sm:text-sm bg-neutral-0 focus:ring-primary-500 border-neutral-300',
                disabled && 'bg-neutral-100 cursor-not-allowed',
              )}
            >
              <div className="flex">
                <Combobox.Input
                  placeholder={placeholder ?? 'Search...'}
                  className="relative w-full p-0 shadow-none sm:text-sm cursor-default border-none focus:ring-0 text-neutral-700"
                  onChange={event => setQuery(event.target.value)}
                  value={open ? query : ((selectedWorkerSkill?.name ?? '') as string)}
                  displayValue={() => (open ? query : selectedWorkerSkill?.name || '')}
                  autoFocus={true}
                  disabled={disabled}
                />
              </div>
            </Combobox.Button>
            {open && !disabled && (
              <Combobox.Options
                static
                className="absolute z-20 w-auto min-w-full max-w-sm py-1 mt-1 overflow-auto text-base rounded-lg shadow-lg bg-neutral-0 max-h-60 ring-1 ring-neutral-900 ring-opacity-5 focus:outline-none sm:text-sm dark:[color-scheme:dark]"
              >
                <TreeView expanded={expandedNodeIds} onNodeToggle={handleToggleExpand}>
                  <TreeOptions opts={filteredOptions} setSelectedOption={setSelectedOption} />
                </TreeView>
              </Combobox.Options>
            )}
          </>
        )}
      </Combobox>
    </div>
  );
};
